import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTime,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchProvinces,
  useUpdateProvince,
} from '@/shared/actions/erp/provinces.actions';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { Province } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';

export type ProvincesPageProps = {};


const ProvincesPage: React.FC<ProvincesPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_provincia);
  const returnUrlProvinces = ROUTER_PATHS.administration.provinciasNav;

  const navigate = useNavigate();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: provincesPagingRes,
    isLoading,
    isRefetching,
  } = useFetchProvinces({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updateProvince = useUpdateProvince({
    navigate,
    returnUrl: returnUrlProvinces,
  });

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Province>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      /* {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */

      {
        accessorKey: 'pais',
        header: 'País',
        size: 200,
        Cell: ({ row }) => row.original?.pais_data?.nombre || 'N/A',
      },

      {
        accessorKey: 'cobertura',
        header: 'Cobertura',
        size: 200,
        enableColumnFilter: false,
        // filterVariant: 'select',
        // enableSorting: false,
        // filterSelectOptions: [
        //   { label: 'SI', value: true },
        //   { label: 'NO', value: false },
        // ],
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.cobertura}
              titulo={row.original.cobertura ? 'SI' : 'NO'}
              setEstadoSwitch={value => {
                if (!hasPermission(PermissionsEnum.api_change_provincia))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado Cobertura',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateProvince.mutate({
                      id: row.original?.id_provincia!,
                      data: { cobertura: !!value } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={provincia => {
                if (!hasPermission(PermissionsEnum.api_change_pais)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updateProvince.mutate({
                      id: row.original?.id_provincia!,
                      data: { estado: !!provincia } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : 'N/A',
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : 'N/A',
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: Province) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Editar Provincia',
      subtitle: '¿Está seguro que desea editar este registro?',
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlProvinces}/editar/${original?.id_provincia}`);
      },
    });
  };

  return (
    <SingleTableBoxScene
      title="Provincias"
      urlRedirect={`${returnUrlProvinces}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_provincia)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="provincia por nombre"
      />

      <GeneralTable<Province>
        columns={columns}
        data={provincesPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={provincesPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_provincia,
          PermissionsEnum.api_view_pais,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_provincia,
          PermissionsEnum.api_view_pais,
        ])}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default ProvincesPage;
